import { compactNumberFormat, roundToClosest } from '@discover-lib/dataFormats';
import { DialogRoot } from '@discover-ui/dialog';
import Cookies from 'js-cookie';
import { useContext, useEffect, useRef, useState } from 'react';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import { useAuthState } from '@/components/context/AuthContext';
import { useModal } from '@/components/context/ModalContext';
import SilverstripeContext from '@/components/context/Silverstripe';
import { useLocalStorageClosedState } from '@/components/hooks/useLocalStorageClosedState';
import FreeCreditExitModalContent from './FreeCreditExitModalContent';

const MODAL_DELAY_MS = 10000;
const PAST_LOGGED_IN_COOKIE = 'PastLoggedIn';
// Preserve dismissals from the former CR-214 experiment key.
const CLOSED_STORAGE_KEY = 'cr214ExitModalClosed';
const TARGET_PAGE_IDENTIFIERS = ['HomePage', 'City', 'CityDiscover', 'Restaurant'] as const;

function isTargetPage(pageClassName?: string, pageTemplate?: string): boolean {
  return TARGET_PAGE_IDENTIFIERS.some(
    (targetPage) => targetPage === pageClassName || targetPage === pageTemplate
  );
}

type FreeCreditExitModalDialogProps = {
  setHasDismissed: (hasDismissed: boolean) => void;
};

function FreeCreditExitModalDialog({ setHasDismissed }: FreeCreditExitModalDialogProps) {
  const {
    global: { siteConfig },
  } = useContext(SilverstripeContext);
  const { track } = useAnalytics();
  const [open, setOpen] = useState(false);
  const hasOpenedRef = useRef(false);
  // useAnalytics returns a fresh track function each render; the ref prevents effect and timer churn.
  const trackRef = useRef(track);

  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  useEffect(() => {
    if (!siteConfig) {
      if (open) {
        setHasDismissed(true);
        trackRef.current('exit_modal_closed', {});
        setOpen(false);
      }

      return;
    }

    if (hasOpenedRef.current) {
      return;
    }

    hasOpenedRef.current = true;
    setOpen(true);
    trackRef.current('exit_modal_opened', {});
  }, [open, setHasDismissed, siteConfig]);

  const handleCtaClick = () => {
    track('exit_modal_cta_clicked', {});
    setHasDismissed(true);
  };

  if (!siteConfig) {
    return null;
  }

  const dinerCountFormatted = `${compactNumberFormat(
    roundToClosest(Math.max(500000, siteConfig.dinerCount ?? 0), 100000)
  )}+`;

  return (
    <DialogRoot
      onOpenChange={(isOpen) => {
        setOpen(isOpen);

        if (!isOpen) {
          setHasDismissed(true);
          track('exit_modal_closed', {});
        }
      }}
      open={open}>
      <FreeCreditExitModalContent
        dinerCountFormatted={dinerCountFormatted}
        currency={siteConfig.currency}
        onCtaClick={handleCtaClick}
      />
    </DialogRoot>
  );
}

export default function FreeCreditExitModal() {
  const {
    global: { siteConfig },
    page,
    pageTemplate,
  } = useContext(SilverstripeContext);
  const { isGuestLoggedIn, isLoggedIn, user } = useAuthState();
  const { isModalOpen } = useModal();
  // PastLoggedIn is device-scoped and excludes signed-out returners from the new-account offer; a fresh browser is treated as new.
  const [hasPastLoggedIn, setHasPastLoggedIn] = useState(true);
  const [isActivated, setIsActivated] = useState(false);
  const [hasDismissed, setHasDismissed] = useLocalStorageClosedState(CLOSED_STORAGE_KEY);

  const hasAuthenticatedUser = isLoggedIn || isGuestLoggedIn || Boolean(user);
  const isEligible =
    Boolean(siteConfig) &&
    !hasAuthenticatedUser &&
    !hasPastLoggedIn &&
    !hasDismissed &&
    isTargetPage(page?.className, typeof pageTemplate === 'string' ? pageTemplate : undefined);

  useEffect(() => {
    setHasPastLoggedIn(Cookies.get(PAST_LOGGED_IN_COOKIE) === '1');
  }, [hasAuthenticatedUser]);

  // ModalContext only tracks legacy ModalPortal modals; pause for those to avoid stacking or counting a competing open.
  useEffect(() => {
    if (!isEligible) {
      if (isActivated) {
        setIsActivated(false);
      }

      return undefined;
    }

    if (isActivated || isModalOpen) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setIsActivated(true);
    }, MODAL_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [isActivated, isEligible, isModalOpen]);

  return isActivated && isEligible ? (
    <FreeCreditExitModalDialog setHasDismissed={setHasDismissed} />
  ) : null;
}
